import type { CaseAgenticMissionLink } from "@/services/api/case-checking";

import { buildMissionStepLabel, missionStepPrefix } from "./missionStageProgress";
import { shouldShowMissionAttention } from "./caseCheckMissionAttention";

/** Stages where automated file-check output is usually ready for display. */
const RESULTS_READY_STAGES = new Set([
  "problem_statements",
  "adjudication",
  "report",
  "close",
]);

export type AgenticCaseProgressCase = {
  hasReview?: boolean;
};

export type CaseCheckProgressDisplayMode = "hidden" | "full" | "minimized";

export function isAgenticMissionBlocked(status: CaseAgenticMissionLink | null | undefined): boolean {
  const taskStatus = (status?.agentic_task_status || "").toLowerCase();
  return taskStatus === "blocked" || taskStatus === "needs_review";
}

/** Mission is waiting on a human or recovery — no automated work in flight. */
export function isCaseCheckMissionPaused(status: CaseAgenticMissionLink | null | undefined): boolean {
  if (!status) return false;
  return shouldShowMissionAttentionState(status) || isAgenticMissionBlocked(status) || Boolean(status.chain_failed) || Boolean(status.intake_failed);
}

function missionHasRunningWork(missionLink: CaseAgenticMissionLink | null | undefined): boolean {
  if (!missionLink) return false;
  if (missionLink.has_running_work) return true;
  const status = (missionLink.agentic_task_status || "").toLowerCase();
  return status === "queued" || status === "working";
}

export function caseCheckProgressIndicatorVariant(
  missionLink: CaseAgenticMissionLink | null | undefined,
  runnerInFlight: boolean,
): "running" | "blocked" | "idle" {
  if (runnerInFlight || missionHasRunningWork(missionLink)) return "running";
  if (isCaseCheckMissionPaused(missionLink)) return "blocked";
  return "idle";
}

export function shouldShowMissionAttentionState(
  status: CaseAgenticMissionLink | null | undefined,
): boolean {
  return shouldShowMissionAttention(status);
}

/**
 * Show progress while the mission is active. When review data is already visible,
 * keep a minimized indicator instead of hiding progress entirely.
 */
export function shouldShowAgenticProgress(
  status: CaseAgenticMissionLink | null | undefined,
  _caseData?: AgenticCaseProgressCase | null,
): boolean {
  if (!status?.is_active) return shouldShowMissionAttentionState(status);
  return true;
}

export function caseCheckProgressDisplayMode(
  status: CaseAgenticMissionLink | null | undefined,
  caseData?: AgenticCaseProgressCase | null,
): CaseCheckProgressDisplayMode {
  if (!shouldShowAgenticProgress(status, caseData) && !shouldShowMissionAttentionState(status)) {
    return "hidden";
  }
  if (shouldShowMissionAttentionState(status)) {
    return "full";
  }
  if (!status?.is_active) return "hidden";
  if (caseData?.hasReview) return "minimized";
  const stage = (status.current_stage || "").toLowerCase();
  if (stage && RESULTS_READY_STAGES.has(stage)) return "minimized";
  return "full";
}

export type AgenticProgressSubLabelOptions = {
  /** Use API-resolved catalogue display name (no runner /ws/tasks stream). */
  preferActiveTaskDisplayName?: boolean;
};

/** Mission-only sub-label (no Step N/M prefix). */
export function agenticProgressSubLabel(
  status: CaseAgenticMissionLink,
  options?: AgenticProgressSubLabelOptions,
): string {
  const taskStatus = (status.agentic_task_status || "").toLowerCase();
  if (taskStatus === "needs_review") {
    const summary = status.attention_summary?.trim() || status.latest_summary?.trim();
    return summary ? `Needs review - ${summary}` : "Needs review";
  }
  if (isAgenticMissionBlocked(status)) {
    const summary = status.attention_summary?.trim() || status.latest_summary?.trim();
    if (summary) return `Blocked - ${summary}`;
    const kind = status.blocked_kind?.trim();
    if (kind) return `Blocked - ${kind.replace(/_/g, " ")}`;
    return "Blocked - needs attention";
  }
  if (status.chain_failed) {
    return "Handoff failed - recover file check";
  }
  if (status.intake_failed || status.attention_kind === "intake_failed") {
    const summary = status.attention_summary?.trim();
    return summary ? `Import failed - ${summary}` : "Import failed - retry file pull";
  }

  const catalogueLabel = status.active_task_display_name?.trim();
  if (options?.preferActiveTaskDisplayName && catalogueLabel) {
    return catalogueLabel;
  }

  const stage = status.current_stage_display?.trim();
  if (stage) return stage;
  if (status.current_stage === "intake") return "Pulling files";
  return "Starting…";
}

/** Short status line while a file check is still running. */
export function agenticProgressLabel(
  status: CaseAgenticMissionLink,
  options?: AgenticProgressSubLabelOptions,
): string {
  return buildMissionStepLabel(missionStepPrefix(status), agenticProgressSubLabel(status, options));
}

function compactStepToken(status: CaseAgenticMissionLink): string | null {
  const order = status.current_stage_order;
  const count = status.stage_count;
  if (order != null && count != null && count > 0) {
    return `${order}/${count}`;
  }
  const prefix = missionStepPrefix(status);
  return prefix ? prefix.replace(/^Step /, "") : null;
}

/** Compact list-row label — step index plus short status only. */
export function agenticCompactProgressLabel(status: CaseAgenticMissionLink): string {
  const step = compactStepToken(status);
  const taskStatus = (status.agentic_task_status || "").toLowerCase();

  if (taskStatus === "needs_review") {
    return step ? `${step} · Review` : "Review";
  }
  if (isAgenticMissionBlocked(status)) {
    return step ? `${step} · Blocked` : "Blocked";
  }
  if (status.chain_failed) {
    return step ? `${step} · Failed` : "Failed";
  }
  if (status.intake_failed || status.attention_kind === "intake_failed") {
    return step ? `${step} · Import failed` : "Import failed";
  }

  if (step) return step;
  const stage = status.current_stage_display?.trim();
  if (stage) return stage.split(/\s+/).slice(0, 2).join(" ");
  if (status.current_stage === "intake") return "Intake";
  return "Running";
}

/** Compact list-row label from a full progress string (runner task stream). */
export function compactCaseProgressLabel(
  fullLabel: string | null | undefined,
  missionLink?: CaseAgenticMissionLink | null,
): string | null {
  if (missionLink && (shouldShowMissionAttentionState(missionLink) || missionLink.is_active)) {
    const missionLabel = agenticCompactProgressLabel(missionLink);
    if (shouldShowMissionAttentionState(missionLink) || !fullLabel) {
      return missionLabel;
    }
  }

  const trimmed = (fullLabel || "").trim();
  if (!trimmed) return missionLink ? agenticCompactProgressLabel(missionLink) : null;

  const stepMatch = trimmed.match(/^Step (\d+\/\d+) - ([^:]+?)(?::.*)?$/);
  if (stepMatch) {
    const verb = stepMatch[2].trim().split(/\s+/)[0];
    return `${stepMatch[1]} · ${verb}`;
  }

  const blockedMatch = trimmed.match(/^Step (\d+\/\d+) - Blocked\b/i);
  if (blockedMatch) return `${blockedMatch[1]} · Blocked`;

  return trimmed.length > 14 ? `${trimmed.slice(0, 12)}…` : trimmed;
}
